import { SelectedRangeCondition } from '@/features/analyze/hooks/useTransitionSelectedRangeResult';
import { addDay, date, dayEnd } from '@formkit/tempo';
import { PlotMouseEvent } from 'plotly.js';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Plot, { PlotParams } from 'react-plotly.js';

/**
 * react-plotly.js v2.6.0の型定義問題を回避するための型アサーション
 *
 * 問題: @types/react-plotly.js v2.6.3とReact 18の型定義に互換性問題があり、
 * 'refs' プロパティが不足しているためJSXコンポーネントとして認識されない
 *
 * 解決策: PlotParamsを使用した型アサーションにより、型安全性を保ちつつ
 * コンポーネントとして使用可能にする
 *
 * TODO: react-plotly.jsまたは@types/react-plotly.jsが更新された際に
 * この型アサーションが不要になる可能性があるため、定期的に確認する
 */
const PlotComponent = Plot as React.ComponentType<PlotParams>;

// GraphQLのTransitionDataPayload型に対応
type HeatmapData =
  | { values: number[][]; xLabels: string[]; yLabels: string[] }
  | null
  | undefined;

const calculatePlotHHeightPx = (itemCount: number) => {
  const eachCellHeight = 13;
  return Math.max(100, itemCount * eachCellHeight);
};

export const HeatmapView = ({
  data,
  setSelectedRangeCondition,
}: {
  data: HeatmapData;
  setSelectedRangeCondition: Dispatch<
    SetStateAction<SelectedRangeCondition | null>
  >;
}) => {
  const { t } = useTranslation();

  const { dataVersion, x, y, z } = useMemo(() => {
    if (!data) {
      return { dataVersion: new Date().getTime(), x: [], y: [], z: [] };
    }

    return {
      dataVersion: new Date().getTime(),
      x: data.xLabels,
      y: data.yLabels,
      z: data.values,
    };
  }, [data]);

  const onClick = (e: PlotMouseEvent) => {
    const country = e.points[0].y as string;
    // @ts-expect-error: pointNumberの型定義が実際と違うため
    const dateNum = e.points[0].pointNumber[1];
    const currentDurationStartDate = date(
      e.points[0].data.x[dateNum] as string
    );
    const nextDurationStartDateStr = e.points[0].data.x[dateNum + 1] as
      | string
      | undefined;

    const currentDurationEndDate = dayEnd(
      nextDurationStartDateStr
        ? addDay(date(nextDurationStartDateStr), -1)
        : new Date() // 次のrangeが存在しない場合は今日までのデータを取得する
    );

    setSelectedRangeCondition({
      country,
      publishedStartAt: currentDurationStartDate,
      publishedEndAt: currentDurationEndDate,
      offset: 0,
    });
  };

  const plotlyHeight = calculatePlotHHeightPx(y.length);

  return (
    <PlotComponent
      key={dataVersion}
      className="w-full"
      data={[
        {
          x,
          y,
          z,
          type: 'heatmap',
          hovertemplate:
            `<b>${t('期間')}</b>: %{x|%Y/%m/%d}<br />` +
            `<b>${t('国名')}</b>: %{y}<br>` +
            `<b>${t('記事数')}</b>: %{z}<extra></extra>`,
          colorscale: [
            [0.0, 'white'],
            [0.0000001, 'rgb(224,247,255)'],
            [0.0499999, 'rgb(224,247,255)'],
            [0.05, 'rgb(129,212,250)'],
            [0.1999999, 'rgb(129,212,250)'],
            [0.2, 'rgb(41,182,246)'],
            [0.3999999, 'rgb(41,182,246)'],
            [0.4, 'rgb(2,136,209)'],
            [0.5999999, 'rgb(2,136,209)'],
            [0.6, 'rgb(2,87,155)'],
            [1.0, 'rgb(2,87,155)'],
          ],
        },
      ]}
      onClick={onClick}
      layout={{
        autosize: true,
        dragmode: false,
        xaxis: {
          side: 'top',
          type: 'date',
          nticks: x?.length === 1 ? 1 : undefined,
          automargin: true,
        },
        yaxis: { automargin: true, autorange: 'reversed', dtick: 1 },
        margin: { b: 0, t: 0 },
        paper_bgcolor: 'transparent',
        height: plotlyHeight,
      }}
      config={{
        displaylogo: false,
        displayModeBar: false,
        responsive: true,
        showAxisDragHandles: false,
      }}
    />
  );
};
